import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  text,
  time,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { classTypes, instructors, levels, rooms } from "./master";

/**
 * Class Templates - Template คลาสเรียน
 * เก็บ template ของคลาสเรียนที่กำหนดประเภท ระดับ และระยะเวลาเริ่มต้น
 * ใช้สำหรับสร้าง class sessions
 */
export const classTemplates = pgTable(
  "class_templates",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ประเภทคลาส - reference ไปยัง class_types */
    classTypeId: text("class_type_id")
      .notNull()
      .references(() => classTypes.id, {
        onDelete: "restrict",
      }),
    /** ระดับความยาก - reference ไปยัง levels */
    levelId: text("level_id")
      .notNull()
      .references(() => levels.id, {
        onDelete: "restrict",
      }),
    /** ระยะเวลาคลาส (นาที) - ใช้เป็นค่า default สำหรับ session */
    duration: integer("duration").notNull(),
    /** คำอธิบายเพิ่มเติม */
    description: text("description"),
    /** สถานะ: active (ใช้งาน) หรือ inactive (ไม่ใช้งาน) */
    status: text("status", {
      enum: ["active", "inactive"],
    })
      .notNull()
      .default("active"),
    /** เปิดรับลงทะเบียนออนไลน์ — ค่าเริ่มต้นปิด จนกว่าพนักงานจะเปิด */
    onlineRegistration: boolean("online_registration").notNull().default(false),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("class_templates_by_class_type_id").on(t.classTypeId),
    index("class_templates_by_level_id").on(t.levelId),
    index("class_templates_by_status").on(t.status),
    index("class_templates_by_online_registration").on(t.onlineRegistration),
    index("class_templates_by_class_type_level").on(t.classTypeId, t.levelId),
    index("class_templates_by_created_by").on(t.createdBy),
  ],
);

/**
 * Class Sessions - Session ที่เกิดขึ้นจริง
 * เก็บข้อมูล session คลาสเรียนที่เกิดขึ้นจริง พร้อมวันที่ เวลา ห้อง ครู และความจุ
 * สามารถย้ายเวลา/ห้อง/ครูได้ และจะบันทึก log ใน schedule_change_logs
 */
export const classSessions = pgTable(
  "class_sessions",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** Template ที่ใช้สร้าง session นี้ - reference ไปยัง class_templates */
    templateId: text("template_id")
      .notNull()
      .references(() => classTemplates.id, {
        onDelete: "restrict",
      }),
    /** วันที่จัดคลาส */
    date: date("date").notNull(),
    /** เวลาเริ่มต้น */
    startTime: time("start_time").notNull(),
    /** เวลาสิ้นสุด */
    endTime: time("end_time").notNull(),
    /**
     * ลักษณะสถานที่: studio = ในสตูดิโอ (ต้องมี room_id), off_site = นอกสถานที่ (room_id ว่างได้)
     */
    locationKind: text("location_kind", {
      enum: ["studio", "off_site"],
    })
      .notNull()
      .default("studio"),
    /** ชื่อ/คำอธิบายสถานที่นอกสถานที่ (เมื่อ location_kind = off_site) */
    offSiteLabel: text("off_site_label"),
    /** ไลยะนอกสถานที่: near = ใกล้, far = ไกล (null เมื่อ studio) */
    offSiteDistance: text("off_site_distance", {
      enum: ["near", "far"],
    }),
    /**
     * มุมมองการขาย/engagement: standard = flow ตัดเครดิตตามปกติได้ (ถ้ามีนักเรียนในระบบ);
     * partner_instructor_only = โครงการ partner / check-in เฉพาะครู ไม่ตัดเครดิตจาก session นี้
     */
    engagementKind: text("engagement_kind", {
      enum: ["standard", "partner_instructor_only"],
    })
      .notNull()
      .default("standard"),
    /** ห้องที่ใช้ - reference ไปยัง rooms (nullable เมื่อ off_site) */
    roomId: text("room_id").references(() => rooms.id, {
      onDelete: "restrict",
    }),
    /** ความจุสูงสุด (จำนวนคน) - soft limit สามารถ override ได้ */
    capacity: integer("capacity").notNull(),
    /**
     * สถานะ session:
     * - scheduled: กำหนดแล้ว
     * - completed: เสร็จสิ้นแล้ว
     * - cancelled: ยกเลิก
     */
    status: text("status", {
      enum: ["scheduled", "completed", "cancelled"],
    })
      .notNull()
      .default("scheduled"),
    /** หมายเหตุเพิ่มเติม */
    notes: text("notes"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("class_sessions_by_template_id").on(t.templateId),
    index("class_sessions_by_date").on(t.date),
    index("class_sessions_by_room_id").on(t.roomId),
    index("class_sessions_by_location_kind").on(t.locationKind),
    index("class_sessions_by_status").on(t.status),
    index("class_sessions_by_date_status").on(t.date, t.status),
    index("class_sessions_by_created_by").on(t.createdBy),
  ],
);

/**
 * Class Session Instructors - ครูผู้สอนหลายคนต่อ 1 class session
 * Many-to-many ระหว่าง class_sessions กับ instructors
 */
export const classSessionInstructors = pgTable(
  "class_session_instructors",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    classSessionId: text("class_session_id")
      .notNull()
      .references(() => classSessions.id, { onDelete: "cascade" }),
    instructorId: text("instructor_id")
      .notNull()
      .references(() => instructors.id, { onDelete: "restrict" }),
    /** lead = ครูหลัก · assist = TA */
    role: text("role", { enum: ["lead", "assist"] })
      .notNull()
      .default("lead"),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("class_session_instructors_session_instructor").on(
      t.classSessionId,
      t.instructorId,
    ),
    index("class_session_instructors_by_session").on(t.classSessionId),
    index("class_session_instructors_by_instructor").on(t.instructorId),
  ],
);

/**
 * Class Template Allowed Instructors - ผู้สอนที่อนุญาตให้สอน template class
 * Many-to-many ระหว่าง class_templates กับ instructors
 * ถ้า template ไม่มี allowed instructors = อนุญาตทุก active instructor
 */
export const classTemplateAllowedInstructors = pgTable(
  "class_template_allowed_instructors",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    templateId: text("template_id")
      .notNull()
      .references(() => classTemplates.id, { onDelete: "cascade" }),
    instructorId: text("instructor_id")
      .notNull()
      .references(() => instructors.id, { onDelete: "restrict" }),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("class_template_allowed_instructors_template_instructor").on(
      t.templateId,
      t.instructorId,
    ),
    index("class_template_allowed_instructors_by_template").on(t.templateId),
    index("class_template_allowed_instructors_by_instructor").on(
      t.instructorId,
    ),
  ],
);
